import React, { useContext, useEffect, useState } from "react";
import pluralize from "pluralize";
import styled from "styled-components";
import { Button, Callout, Slider, Spinner } from "@blueprintjs/core";
import { Intent, MenuItem } from "@blueprintjs/core";
import { ItemRenderer, Select } from "@blueprintjs/select";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { AppToaster, LoadingView } from "@components";
import { useContributors } from "@hooks";
import { UserContext } from "@providers";
import { OrgData } from "@types";

import { claimLicense, fetchLicense } from "@pages/settings/api";
import { CheckoutForm } from "@pages/settings/components/CheckoutForm";
import { get_stripe_pk, get_team_seat_id } from "@pages/settings/constants";
import { LicenseData } from "@pages/settings/types";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(get_stripe_pk());

const max_seats = 20; // TODO: should this be dynamic?

const Info = styled.div`
  margin: 20px 0;
`;

const SelectContainer = styled.div`
  margin: 10px 0 35px 0;
`;

type UpgradePanelProps = {
  org: OrgData;
  hasTeamTier: Boolean;
  licenseKey: string;
  onLicenseClaimed: () => void;
};

interface ClaimLicenseProps {
  org: OrgData;
  licenseKey: string;
  onLicenseClaimed: () => void;
}

type Props = {
  org: OrgData;
  seats: number;
};

type SeatProps = {
  org: OrgData;
  hasTeamsTier: Boolean;
  seats: number;
};

export const UpgradePanel: React.FC<UpgradePanelProps> = ({
  org,
  hasTeamTier,
  licenseKey,
  onLicenseClaimed,
}) => {
  const { isLoading, data: numContributors } = useContributors();
  const seats = numContributors !== undefined ? numContributors : 0;

  return licenseKey ? (
    <ApplyUpgradePanel
      org={org}
      licenseKey={licenseKey}
      onLicenseClaimed={onLicenseClaimed}
    />
  ) : isLoading ? (
    <LoadingView />
  ) : hasTeamTier ? (
    <TeamTierPanel org={org} seats={seats} />
  ) : (
    <CommunityTierPanel org={org} seats={seats} />
  );
};

export const SeatPanel: React.FC<SeatProps> = ({
  org,
  hasTeamsTier,
  seats,
}) => {
  const scm_connected = org.source_type !== undefined;
  return (
    <div>
      {!scm_connected ? (
        <Callout
          intent={Intent.PRIMARY}
          style={{
            fontSize: "var(--global-font-size-s)",
            marginTop: "20px",
          }}
        >
          Please connect Semgrep to your SCM in order to track your seat usage.
        </Callout>
      ) : (
        <>
          {hasTeamsTier ? (
            <Info>
              You are currently using <b>{seats}</b> {pluralize("seat", seats)}.{" "}
            </Info>
          ) : (
            <Info>
              You are currently using <b>{seats}</b> out of {max_seats} seats.{" "}
            </Info>
          )}
          <Info>
            Your usage is based on the number of developers who have made a
            commit in the past 30 days. Look unusual? Contact us!
          </Info>
        </>
      )}
    </div>
  );
};

export const CommunityTierPanel: React.FC<Props> = ({ org, seats }) => {
  const seat_id = get_team_seat_id();
  const seat_price = 40;
  const [quantity, setQuantity] = useState<number>(1);
  const [price, setPrice] = useState<number>(seat_price);
  const [spinner, setSpinner] = useState<boolean>(false);

  const scm_connected = org.source_type !== undefined;

  return (
    <div style={{ maxWidth: "600px" }}>
      {scm_connected && seats > max_seats && (
        <Callout
          intent={Intent.DANGER}
          style={{
            fontSize: "var(--global-font-size-s)",
            marginTop: "20px",
          }}
        >
          You are currently over the usage limit for Semgrep Community Tier.
          Please consider upgrading tiers.
        </Callout>
      )}

      <Info>
        Your organization, <b>{org.name}</b>, is using Semgrep Community Tier.{" "}
      </Info>

      <SeatPanel org={org} hasTeamsTier={false} seats={seats} />

      <Info>
        For more information, visit our{" "}
        <a
          href="https://semgrep.dev/docs/faq/#billing--pricing"
          target="_blank"
          rel="noopener noreferrer"
        >
          FAQ
        </a>
        .
      </Info>
      <div>
        <span>Select the number of seats you wish to purchase:</span>
        <Slider
          min={1}
          max={200}
          initialValue={1}
          labelStepSize={199}
          value={quantity}
          onChange={(qty) => {
            setQuantity(qty);
            setPrice(seat_price * qty);
          }}
        />
        <span>
          <b>Total: ${price}/mo</b>
        </span>
      </div>
      <br />
      <Elements stripe={stripePromise}>
        <CheckoutForm
          priceId={seat_id}
          useSpinner={spinner}
          setSpinner={setSpinner}
          quantity={quantity}
        />
      </Elements>
      <Info>
        *r2c has the right to audit usage and limit Semgrep Team access if more
        developers are committing code than have been paid for.
      </Info>
    </div>
  );
};

type MailToProps = {
  email: string;
  subject?: string;
  body?: string;
  children: React.ReactNode;
};

const Mailto: React.FC<MailToProps> = ({
  email,
  subject = "",
  body = "",
  children,
}) => {
  let params = subject || body ? "?" : "";
  if (subject) params += `subject=${encodeURIComponent(subject)}`;
  if (body) params += `${subject ? "&" : ""}body=${encodeURIComponent(body)}`;

  return <a href={`mailto:${email}${params}`}>{children}</a>;
};

export const TeamTierPanel: React.FC<Props> = ({ org, seats }) => {
  return (
    <div style={{ maxWidth: "600px" }}>
      <Info>
        Your organization, <b>{org.name}</b>, is using Semgrep Team Tier.{" "}
      </Info>

      <SeatPanel org={org} hasTeamsTier={true} seats={seats} />

      <Info>
        To update or cancel your Team subscription please contact{" "}
        <Mailto
          email="billing@r2c.dev"
          subject={`Semgrep for ${org.name} (${org.id})`}
        >
          billing@r2c.dev
        </Mailto>{" "}
        and we will be happy to help you.
      </Info>
    </div>
  );
};

const Details = styled.div`
  margin: 5px 20px;
`;

const InfoLabel = styled.label`
  font-weight: bold;
`;

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getDaysRemaining = (expDateStr: string) => {
  const expDate = new Date(Date.parse(expDateStr + "T00:00:00.000Z")); // Adding the UTC time prevents unexpected timezone conversions
  const dateDiff = Math.abs(expDate.getTime() - Date.now());
  return Math.ceil(dateDiff / (1000 * 60 * 60 * 24));
};

export const ApplyUpgradePanel: React.FC<ClaimLicenseProps> = ({
  org,
  licenseKey,
  onLicenseClaimed,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isLoadError, setIsLoadError] = useState<boolean>(false);
  const [isClaimError, setIsClaimError] = useState<boolean>(false);
  const [claimError, setClaimError] = useState<string>();
  const [license, setLicense] = useState<LicenseData>();
  const [isClaiming, setIsClaiming] = useState<boolean>(false);
  const { user } = useContext(UserContext);
  const multipleOrgs = user !== undefined && user.orgs.length > 1;
  const [claimOrg, setClaimOrg] = useState<OrgData | undefined>(
    // If there is only 1 org it should be selected
    multipleOrgs ? undefined : user?.orgs[0]
  );

  useEffect(() => {
    setLoading(true);
    fetchLicense(licenseKey)
      .then((license_resp) => {
        setLicense(license_resp.license);
      })
      .catch(() => {
        setIsLoadError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [licenseKey]);

  const onOrgChange = (org: OrgData, _?: React.SyntheticEvent<HTMLElement>) => {
    setClaimOrg(org);
  };

  const handleSubmit = () => {
    if (!claimOrg || !claimOrg.id) {
      setIsClaimError(true);
      setClaimError("No organization selected");
      return;
    }

    setIsClaiming(true);
    claimLicense(licenseKey, claimOrg.id)
      .then(() => {
        AppToaster.show({
          message: license!.is_trial
            ? `Congratulations! Your trial has started`
            : `The upgrade has been applied to organization ${claimOrg?.name}`,
          intent: Intent.SUCCESS,
          onDismiss: onLicenseClaimed,
        });
      })
      .catch((err) => {
        setIsClaimError(true);
        const errMsg = err.body?.error;
        if (errMsg) setClaimError(errMsg);
      });
  };

  const orgRenderer: ItemRenderer<OrgData> = (selectOrg, { handleClick }) => (
    <MenuItem
      key={selectOrg.id}
      onClick={handleClick}
      text={selectOrg.name + (selectOrg.id === org.id ? " (current)" : "")}
    />
  );

  return (
    <div>
      {loading ? (
        <Spinner />
      ) : isLoadError ? (
        <div className="bp3-callout bp3-intent-danger">
          That upgrade was not found or has already been applied to an
          organization. Please reach out to the person who gave you the link or
          contact us at support@r2c.dev.
        </div>
      ) : isClaimError ? (
        <div className="bp3-callout bp3-intent-danger">
          There was a problem applying the upgrade
          {claimError ? `: ${claimError}` : ""}. Please contact support@r2c.dev
          if the problem continues.
        </div>
      ) : (
        <div>
          {license!.is_trial ? (
            <Info>
              You are about to start the following Semgrep trial:
              <Details>
                <div>
                  <InfoLabel>Tier:</InfoLabel>{" "}
                  {capitalize(license!.license_type)}
                </div>
                <div>
                  <InfoLabel>Length:</InfoLabel> {license!.license_days} days
                </div>
              </Details>
            </Info>
          ) : (
            <Info>
              You're almost there! You have purchased the following upgrade:
              <Details>
                <div>
                  <InfoLabel>Tier:</InfoLabel>{" "}
                  {capitalize(license!.license_type)}
                </div>
                <div>
                  <InfoLabel>Seats:</InfoLabel> {license!.license_count} users
                </div>
                <div>
                  <InfoLabel>Last Day:</InfoLabel> {license!.expiration_date}{" "}
                  (in {getDaysRemaining(license!.expiration_date)} days)
                </div>
              </Details>
            </Info>
          )}
          <Info>
            Please select the organization you would like the{" "}
            {license!.is_trial ? "trial" : "upgrade"} to be applied to:
            <SelectContainer>
              <Select<OrgData>
                items={user!.orgs}
                itemRenderer={orgRenderer}
                onItemSelect={onOrgChange}
                filterable={false}
              >
                <Button>
                  {claimOrg ? claimOrg.name : "—select—"}&nbsp;&nbsp;
                  <FontAwesomeIcon icon={faCaretDown} />
                </Button>
              </Select>
            </SelectContainer>
          </Info>

          <Button
            disabled={claimOrg === undefined || isClaiming}
            onClick={() => {
              handleSubmit();
            }}
            intent={Intent.PRIMARY}
          >
            {license!.is_trial ? "Start Trial" : "Apply upgrade"}
          </Button>
        </div>
      )}
    </div>
  );
};
